import * as React from 'react';
import { ApiErrorCode } from '@ooh/contracts';
import type { Offer } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DraftUnitsGate } from './draft-units-gate';
import { useNavigate } from '@tanstack/react-router';
import {
  FinalizationPanel,
  ReservePreliminaryDialog,
  ReservePreliminaryWorkGate,
} from '@ooh/screens';
import {
  buildOrderDocumentFileName,
  isOrderDocumentDownloadError,
  useDownloadOrderDocument,
} from '../hooks/use-download-order-document';
import { ConfirmCampaignDialog } from './confirm-campaign-dialog';
import { RecordSaleDialog } from './record-sale-dialog';

export interface OfferFinalizationSectionProps {
  canConfirmCampaign?: boolean;
  canRecordSale?: boolean;
  hasUnsecuredUnits?: boolean;
  isAccessResolved?: boolean;
  /** Offer edit access (view+edit) — gates the preliminary-reservation
   * action's visibility independently of `canConfirmCampaign` (offer-accept-
   * reservation). */
  canEdit?: boolean;
  /** `reservation.manage` — the second half of the preliminary-reservation gate. */
  canManageReservation?: boolean;
  /** The offer's currently active `ACCEPTED` version, threaded from the
   * route the same way `canConfirmCampaign` flows through workspace-access.ts
   * / workspace-shell.tsx. `null` when the offer carries no accepted version
   * (e.g. status isn't ACCEPTED). */
  activeAcceptedVersionId?: number | null;
  activeAcceptedVersionNumber?: number | null;
  offer: Offer;
}

/** Maps each order-document ApiErrorCode to its distinct translated toast key — no
 * silent fallback to the generic toast (offer-order-document-export spec). The
 * mixed-currency 422 carries no distinct errorCode (statusToErrorCode's 422 default,
 * apps/api/src/common/filters/http-exception.filter.ts), so it is matched by
 * statusCode alone once the named codes have been ruled out. An unpriced
 * accepted unit is no longer a refusal case (bug fix,
 * offer-order-document-export) — see the post-download unpricedCount warning
 * toast in handleDownloadOrderDocument instead. */
function orderDocumentToastKey(
  error: unknown,
):
  | 'orderDocumentNoAcceptedVersion'
  | 'orderDocumentNoAcceptedUnits'
  | 'orderDocumentTooManyUnits'
  | 'orderDocumentMixedCurrency'
  | 'orderDocumentError' {
  if (!isOrderDocumentDownloadError(error)) {
    return 'orderDocumentError';
  }
  switch (error.errorCode) {
    case ApiErrorCode.NO_ACCEPTED_VERSION:
      return 'orderDocumentNoAcceptedVersion';
    case ApiErrorCode.NO_ACCEPTED_UNITS:
      return 'orderDocumentNoAcceptedUnits';
    case ApiErrorCode.TOO_MANY_UNITS:
      return 'orderDocumentTooManyUnits';
    default:
      return error.statusCode === 422 ? 'orderDocumentMixedCurrency' : 'orderDocumentError';
  }
}

/** Whether the caller may reserve preliminarily at all — independent of
 * whether the active accepted version id has resolved yet. Extracted so
 * `OfferFinalizationSection`'s own complexity doesn't carry this chain (see
 * reserve-preliminary-work-gate.tsx's doc comment for why the fetch is never
 * called unconditionally). */
function canReservePreliminary(
  offer: Offer,
  canEdit: boolean,
  canManageReservation: boolean,
): boolean {
  return canEdit && canManageReservation && offer.status === 'ACCEPTED';
}

interface FinalizationPanelWithReserveGateProps {
  offer: Offer;
  hasUnsecuredUnits: boolean;
  canConfirmCampaign: boolean;
  canRecordSale: boolean;
  canEdit: boolean;
  canManageReservation: boolean;
  activeAcceptedVersionId: number | null;
  isAccessResolved: boolean;
  isDownloading: boolean;
  onConfirmCampaign: () => void;
  onSecureRemaining: () => void;
  onRecordSale: () => void;
  onDownloadOrderDocument: () => void;
  onReservePreliminary: () => void;
}

/** Wraps `FinalizationPanel` with `ReservePreliminaryWorkGate` only when the
 * caller may actually reserve, so every other status/permission combination
 * (the overwhelming majority of renders) skips the extra fetch entirely —
 * extracted out of `OfferFinalizationSection` to keep that component's own
 * complexity under the lint budget. */
function FinalizationPanelWithReserveGate({
  offer,
  hasUnsecuredUnits,
  canConfirmCampaign,
  canRecordSale,
  canEdit,
  canManageReservation,
  activeAcceptedVersionId,
  isAccessResolved,
  isDownloading,
  onConfirmCampaign,
  onSecureRemaining,
  onRecordSale,
  onDownloadOrderDocument,
  onReservePreliminary,
}: FinalizationPanelWithReserveGateProps) {
  function renderPanel(hasReservePreliminaryWork: boolean, hasDraftUnits: boolean) {
    return (
      <FinalizationPanel
        status={offer.status}
        campaignNumber={offer.campaignNumber}
        isTrashed={offer.trashedAt != null}
        hasDraftUnits={hasDraftUnits}
        hasUnsecuredUnits={hasUnsecuredUnits}
        canConfirmCampaign={canConfirmCampaign}
        canRecordSale={canRecordSale}
        canEditOffer={canEdit}
        canManageReservation={canManageReservation}
        hasReservePreliminaryWork={hasReservePreliminaryWork}
        isAccessResolved={isAccessResolved}
        isDownloadingOrderDocument={isDownloading}
        onConfirmCampaign={onConfirmCampaign}
        onSecureRemaining={onSecureRemaining}
        onRecordSale={onRecordSale}
        onDownloadOrderDocument={onDownloadOrderDocument}
        onReservePreliminary={onReservePreliminary}
      />
    );
  }

  const canReserve = canReservePreliminary(offer, canEdit, canManageReservation);

  if (canReserve && activeAcceptedVersionId != null) {
    return (
      <ReservePreliminaryWorkGate offerId={String(offer.id)} versionId={activeAcceptedVersionId}>
        {(hasWork) => renderPanel(hasWork, false)}
      </ReservePreliminaryWorkGate>
    );
  }

  // A draft's nośnik count is unreachable from a canReserve branch (that
  // requires ACCEPTED); skip the fetch entirely for a trashed draft, which
  // renders nothing anyway.
  if (offer.status === 'DRAFT' && offer.trashedAt == null) {
    return (
      <DraftUnitsGate offerId={offer.id}>
        {(hasDraftUnits) => renderPanel(!canReserve, hasDraftUnits)}
      </DraftUnitsGate>
    );
  }

  // Permitted to reserve but the active accepted version id hasn't resolved
  // yet — treat as "no work yet" instead of rendering an enabled button
  // whose dialog can never open (offer-accept-reservation).
  return renderPanel(!canReserve, false);
}

/** Resolves every optional prop's default — extracted so the many default
 * parameter values (each counted as its own branch by the `complexity` lint
 * rule) don't land inside `OfferFinalizationSection`'s own signature, the
 * same trick `workspace-shell.tsx`'s `defaultFalse()` uses. */
function resolveFinalizationSectionDefaults(props: OfferFinalizationSectionProps) {
  return {
    canConfirmCampaign: props.canConfirmCampaign ?? false,
    canRecordSale: props.canRecordSale ?? false,
    hasUnsecuredUnits: props.hasUnsecuredUnits ?? false,
    isAccessResolved: props.isAccessResolved ?? true,
    canEdit: props.canEdit ?? false,
    canManageReservation: props.canManageReservation ?? false,
    activeAcceptedVersionId: props.activeAcceptedVersionId ?? null,
    activeAcceptedVersionNumber: props.activeAcceptedVersionNumber ?? null,
  };
}

/**
 * Owns the finalization panel's dialogs and the order-document download —
 * the finalization actions live only here, as a sibling of `OfferHeader` in
 * `WorkspaceShell`, never in the header itself (offer-approval /
 * "Finalization path in the offer workspace").
 */
export function OfferFinalizationSection(props: OfferFinalizationSectionProps) {
  const { offer } = props;
  const {
    canConfirmCampaign,
    canRecordSale,
    hasUnsecuredUnits,
    isAccessResolved,
    canEdit,
    canManageReservation,
    activeAcceptedVersionId,
    activeAcceptedVersionNumber,
  } = resolveFinalizationSectionDefaults(props);
  const { t } = useTranslation('offers');
  const { download: downloadOrderDocument, isDownloading } = useDownloadOrderDocument();
  const navigate = useNavigate();
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isSaleOpen, setIsSaleOpen] = React.useState(false);
  const [isReserveOpen, setIsReserveOpen] = React.useState(false);

  async function handleDownloadOrderDocument() {
    try {
      const unpricedCount = await downloadOrderDocument(
        String(offer.id),
        buildOrderDocumentFileName(offer),
      );
      // Success toast is intentionally warning, not error -- the document
      // still generated; this only flags that some lines have no price yet
      // (bug fix, offer-order-document-export).
      if (unpricedCount > 0) {
        toast.warning(
          t('workspace.header.toasts.orderDocumentUnpricedWarning', { count: unpricedCount }),
        );
      }
    } catch (error) {
      console.error('[order-document-download]', offer.id, error);
      toast.error(t(`workspace.header.toasts.${orderDocumentToastKey(error)}`));
    }
  }

  return (
    <>
      <FinalizationPanelWithReserveGate
        offer={offer}
        hasUnsecuredUnits={hasUnsecuredUnits}
        canConfirmCampaign={canConfirmCampaign}
        canRecordSale={canRecordSale}
        canEdit={canEdit}
        canManageReservation={canManageReservation}
        activeAcceptedVersionId={activeAcceptedVersionId}
        isAccessResolved={isAccessResolved}
        isDownloading={isDownloading}
        onConfirmCampaign={() => setIsConfirmOpen(true)}
        onSecureRemaining={() => setIsConfirmOpen(true)}
        onRecordSale={() => setIsSaleOpen(true)}
        onDownloadOrderDocument={() => void handleDownloadOrderDocument()}
        onReservePreliminary={() => setIsReserveOpen(true)}
      />

      {isConfirmOpen ? (
        <ConfirmCampaignDialog offer={offer} onClose={() => setIsConfirmOpen(false)} />
      ) : null}

      {isSaleOpen ? <RecordSaleDialog offer={offer} onClose={() => setIsSaleOpen(false)} /> : null}

      {isReserveOpen && activeAcceptedVersionId != null ? (
        <ReservePreliminaryDialog
          open={isReserveOpen}
          onClose={() => setIsReserveOpen(false)}
          offerId={String(offer.id)}
          versionId={activeAcceptedVersionId}
          versionNumber={activeAcceptedVersionNumber ?? 0}
          onNavigateToUnit={(mediaUnitId) =>
            void navigate({ to: '/media-units/$id', params: { id: String(mediaUnitId) } })
          }
        />
      ) : null}
    </>
  );
}
